import React, { useState, useEffect, useRef, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import { useRealtime } from '../../../contexts/RealtimeContext';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useLanguage } from '../../../contexts/LanguageContext';

const OCCUPANCY_THRESHOLD = 90; // percent

export default function OccupancyAlertBanner() {
  const { buildingOccupancyStats, occupancyMetrics } = useRealtime();
  const { notifyAdmins } = useNotifications();
  const { language } = useLanguage();

  const [dismissed, setDismissed] = useState(new Set());
  const notifiedRef = useRef(new Set());

  const buildingStats = buildingOccupancyStats || [];

  const criticalBuildings = useMemo(
    () => buildingStats?.filter(b => b?.rate >= OCCUPANCY_THRESHOLD),
    [buildingStats]
  );

  const warningBuildings = useMemo(
    () => buildingStats?.filter(b => b?.rate >= 80 && b?.rate < OCCUPANCY_THRESHOLD),
    [buildingStats]
  );

  const overallRate = parseFloat(occupancyMetrics?.occupancyRate || 0);
  const overallCritical = overallRate >= OCCUPANCY_THRESHOLD;
  const overallWarning = overallRate >= 80 && overallRate < OCCUPANCY_THRESHOLD;

  // Fire admin notifications for buildings crossing 90% — once per session per building
  useEffect(() => {
    criticalBuildings?.forEach(b => {
      const key = `building-${b?.building}-${Math.floor(b?.rate / 5) * 5}`;
      if (!notifiedRef?.current?.has(key)) {
        notifiedRef?.current?.add(key);
        notifyAdmins?.({
          title: language === 'fr'
            ? `⚠️ Alerte capacité — Bâtiment ${b?.building}`
            : `⚠️ Capacity Alert — Building ${b?.building}`,
          message: language === 'fr'
            ? `Le bâtiment ${b?.building} a atteint ${b?.rate}% d'occupation (${b?.occupied}/${b?.total} chambres). Seuil de 90% dépassé.`
            : `Building ${b?.building} has reached ${b?.rate}% occupancy (${b?.occupied}/${b?.total} rooms). 90% threshold exceeded.`,
          type: 'warning',
          relatedTable: 'rooms',
        });
      }
    });
  }, [criticalBuildings, notifyAdmins, language]);

  // Fire overall alert when total occupancy crosses 90%
  useEffect(() => {
    if (overallCritical) {
      const key = `overall-${Math.floor(overallRate / 5) * 5}`;
      if (!notifiedRef?.current?.has(key)) {
        notifiedRef?.current?.add(key);
        notifyAdmins?.({
          title: language === 'fr'
            ? `🚨 Alerte globale — Occupation à ${overallRate}%`
            : `🚨 Global Alert — Occupancy at ${overallRate}%`,
          message: language === 'fr' ? `L'occupation globale a dépassé le seuil de 90% (${occupancyMetrics?.occupiedRooms}/${occupancyMetrics?.totalRooms} chambres occupées).`
            : `Overall occupancy exceeded 90% threshold (${occupancyMetrics?.occupiedRooms}/${occupancyMetrics?.totalRooms} rooms occupied).`,
          type: 'error',
          relatedTable: 'rooms',
        });
      }
    }
  }, [overallCritical, overallRate, occupancyMetrics, notifyAdmins, language]);

  const visibleCritical = criticalBuildings?.filter(b => !dismissed?.has(`c-${b?.building}`));
  const visibleWarning = warningBuildings?.filter(b => !dismissed?.has(`w-${b?.building}`));
  const showOverall = (overallCritical || overallWarning) && !dismissed?.has('overall');

  if (!showOverall && visibleCritical?.length === 0 && visibleWarning?.length === 0) return null;

  return (
    <div className="space-y-2">
      {/* Overall occupancy alert */}
      {showOverall && (
        <div
          className={`flex items-start gap-3 px-4 py-3 rounded-xl border ${
            overallCritical
              ? 'bg-red-50 border-red-200' :'bg-amber-50 border-amber-200'
          }`}
        >
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
            overallCritical ? 'bg-red-100' : 'bg-amber-100'
          }`}>
            <Icon
              name={overallCritical ? 'AlertTriangle' : 'AlertCircle'}
              size={16}
              className={overallCritical ? 'text-red-600' : 'text-amber-600'}
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold ${overallCritical ? 'text-red-800' : 'text-amber-800'}`}>
              {overallCritical
                ? (language === 'fr' ? `🚨 Capacité critique — ${overallRate}% d'occupation globale` : `🚨 Critical Capacity — ${overallRate}% Overall Occupancy`)
                : (language === 'fr' ? `⚠️ Occupation élevée — ${overallRate}% d'occupation globale` : `⚠️ High Occupancy — ${overallRate}% Overall Occupancy`)}
            </p>
            <p className={`text-xs mt-0.5 ${overallCritical ? 'text-red-600' : 'text-amber-600'}`}>
              {language === 'fr'
                ? `${occupancyMetrics?.occupiedRooms} chambres occupées sur ${occupancyMetrics?.totalRooms} — ${occupancyMetrics?.availableRooms} disponibles`
                : `${occupancyMetrics?.occupiedRooms} of ${occupancyMetrics?.totalRooms} rooms occupied — ${occupancyMetrics?.availableRooms} available`}
            </p>
          </div>
          <button
            onClick={() => setDismissed(prev => new Set([...prev, 'overall']))}
            className={`flex-shrink-0 p-1 rounded hover:bg-black/10 transition-colors ${
              overallCritical ? 'text-red-500' : 'text-amber-500'
            }`}
            aria-label="Dismiss"
          >
            <Icon name="X" size={14} />
          </button>
        </div>
      )}
      {/* Per-building critical alerts (≥90%) */}
      {visibleCritical?.map(b => (
        <div
          key={`c-${b?.building}`}
          className="flex items-start gap-3 px-4 py-3 rounded-xl border bg-red-50 border-red-200"
        >
          <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
            <Icon name="AlertTriangle" size={16} className="text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-red-800">
              {language === 'fr'
                ? `🚨 Bâtiment ${b?.building} — ${b?.rate}% d'occupation (seuil 90% dépassé)`
                : `🚨 Building ${b?.building} — ${b?.rate}% Occupancy (90% threshold exceeded)`}
            </p>
            <div className="flex items-center gap-3 mt-1.5">
              <div className="flex-1 h-1.5 bg-red-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-red-500 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(b?.rate, 100)}%` }}
                />
              </div>
              <span className="text-xs font-mono text-red-600 flex-shrink-0">
                {b?.occupied}/{b?.total}
              </span>
            </div>
          </div>
          <button
            onClick={() => setDismissed(prev => new Set([...prev, `c-${b.building}`]))}
            className="flex-shrink-0 p-1 rounded hover:bg-black/10 transition-colors text-red-500"
            aria-label="Dismiss"
          >
            <Icon name="X" size={14} />
          </button>
        </div>
      ))}
      {/* Per-building warning alerts (80–89%) */}
      {visibleWarning?.map(b => (
        <div
          key={`w-${b?.building}`}
          className="flex items-start gap-3 px-4 py-3 rounded-xl border bg-amber-50 border-amber-200"
        >
          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Icon name="AlertCircle" size={16} className="text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800">
              {language === 'fr'
                ? `⚠️ Bâtiment ${b?.building} — ${b?.rate}% d'occupation (approche du seuil)`
                : `⚠️ Building ${b?.building} — ${b?.rate}% Occupancy (approaching threshold)`}
            </p>
            <div className="flex items-center gap-3 mt-1.5">
              <div className="flex-1 h-1.5 bg-amber-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-500 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(b?.rate, 100)}%` }}
                />
              </div>
              <span className="text-xs font-mono text-amber-600 flex-shrink-0">
                {b?.occupied}/{b?.total}
              </span>
            </div>
          </div>
          <button
            onClick={() => setDismissed(prev => new Set([...prev, `w-${b.building}`]))}
            className="flex-shrink-0 p-1 rounded hover:bg-black/10 transition-colors text-amber-500"
            aria-label="Dismiss"
          >
            <Icon name="X" size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
